// Secondary condition auto-suggestions
// Maps primary DC codes to commonly secondary-connected conditions
// Each entry: { code, name, system, reason }

const secondaryMap = {
  // PTSD secondaries
  "9411": [
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Depression is frequently secondary to PTSD. VA recognizes the comorbidity." },
    { code: "9400", name: "Generalized anxiety disorder", system: "mental", reason: "Anxiety disorders commonly develop alongside or secondary to PTSD." },
    { code: "6847", name: "Sleep apnea syndromes", system: "respiratory", reason: "Sleep disturbance from PTSD can aggravate or contribute to sleep apnea." },
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Stress-related migraines are commonly secondary to PTSD." },
    { code: "7319", name: "IBS / irritable colon", system: "digestive", reason: "GI conditions are commonly secondary to mental health conditions via the gut-brain axis." },
    { code: "7206", name: "GERD / esophageal reflux", system: "digestive", reason: "Stress and PTSD medications can aggravate GERD." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "ED is commonly secondary to PTSD and/or PTSD medications (SSRIs)." },
    { code: "7101", name: "Hypertension", system: "cardio", reason: "Chronic stress from PTSD can contribute to hypertension." },
  ],
  // Depression secondaries
  "9434": [
    { code: "9411", name: "PTSD", system: "mental", reason: "PTSD and depression frequently co-occur." },
    { code: "6847", name: "Sleep apnea syndromes", system: "respiratory", reason: "Depression-related sleep disturbance can aggravate sleep apnea." },
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Migraines commonly associated with depression." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "ED commonly secondary to depression and antidepressant medications." },
  ],
  // Anxiety secondaries
  "9400": [
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Depression frequently co-occurs with anxiety." },
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Tension and stress-related headaches secondary to anxiety." },
    { code: "7319", name: "IBS / irritable colon", system: "digestive", reason: "IBS is commonly secondary to anxiety via gut-brain connection." },
    { code: "7206", name: "GERD", system: "digestive", reason: "Anxiety and anxiety medications can worsen GERD." },
  ],
  // Lumbar strain / back
  "5237": [
    { code: "8520", name: "Sciatic nerve radiculopathy", system: "neurological", reason: "Radiculopathy commonly results from lumbar disc/strain conditions." },
    { code: "5243", name: "Intervertebral disc syndrome (IVDS)", system: "musculoskeletal", reason: "IVDS can develop from chronic lumbar strain." },
    { code: "5242", name: "Degenerative arthritis of spine", system: "musculoskeletal", reason: "DDD/arthritis commonly develops in areas of prior strain." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "ED can be secondary to lumbar radiculopathy affecting nerve function." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic pain from back conditions commonly causes secondary depression." },
    { code: "5260", name: "Knee limitation of flexion", system: "musculoskeletal", reason: "Altered gait from back condition can cause secondary knee problems." },
  ],
  // DDD / Degenerative disc
  "5242": [
    { code: "8520", name: "Sciatic nerve radiculopathy", system: "neurological", reason: "Disc degeneration commonly causes nerve compression and radiculopathy." },
    { code: "5243", name: "IVDS", system: "musculoskeletal", reason: "Degenerative changes lead to disc syndrome." },
    { code: "5237", name: "Cervical/lumbar strain", system: "musculoskeletal", reason: "Compensatory strain in adjacent spinal segments." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic pain commonly causes secondary depression." },
  ],
  // IVDS
  "5243": [
    { code: "8520", name: "Sciatic nerve radiculopathy", system: "neurological", reason: "Disc herniation directly causes radiculopathy." },
    { code: "5237", name: "Lumbar/cervical strain", system: "musculoskeletal", reason: "Compensatory strain from disc condition." },
    { code: "7534", name: "Neurogenic bladder", system: "genitourinary", reason: "Severe disc herniation can affect bladder nerve function." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "Nerve involvement from disc disease can cause ED." },
  ],
  // Sciatica
  "8520": [
    { code: "5276", name: "Flatfoot / pes planus", system: "musculoskeletal", reason: "Altered gait from sciatic pain can cause or worsen flatfoot." },
    { code: "5260", name: "Knee limitation of flexion", system: "musculoskeletal", reason: "Compensatory gait changes can cause secondary knee problems." },
    { code: "5271", name: "Ankle limitation of motion", system: "musculoskeletal", reason: "Nerve damage can affect ankle function." },
  ],
  // Knee conditions
  "5257": [
    { code: "5003", name: "Degenerative arthritis", system: "musculoskeletal", reason: "Instability causes accelerated joint degeneration." },
    { code: "5260", name: "Knee limitation of flexion", system: "musculoskeletal", reason: "Instability and pain commonly limit knee flexion." },
    { code: "5261", name: "Knee limitation of extension", system: "musculoskeletal", reason: "Rate limitation of extension separately from instability." },
    { code: "5271", name: "Ankle limitation of motion", system: "musculoskeletal", reason: "Altered gait from knee condition can affect ankle." },
    { code: "5237", name: "Lumbar strain", system: "musculoskeletal", reason: "Altered gait from knee condition can cause secondary back problems." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic pain and mobility limitation can cause depression." },
  ],
  "5260": [
    { code: "5261", name: "Knee limitation of extension", system: "musculoskeletal", reason: "Both flexion and extension can be rated separately for the same knee." },
    { code: "5257", name: "Knee instability", system: "musculoskeletal", reason: "Instability is rated separately from limitation of motion." },
    { code: "5003", name: "Degenerative arthritis", system: "musculoskeletal", reason: "Arthritis commonly causes limitation of motion." },
  ],
  // Sleep apnea
  "6847": [
    { code: "9411", name: "PTSD", system: "mental", reason: "PTSD-related sleep disturbance can aggravate sleep apnea." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Sleep deprivation from apnea commonly causes depression." },
    { code: "7101", name: "Hypertension", system: "cardio", reason: "Sleep apnea is a recognized cause of secondary hypertension." },
    { code: "7005", name: "Coronary artery disease", system: "cardio", reason: "Sleep apnea increases cardiovascular disease risk." },
    { code: "6260", name: "Tinnitus", system: "hearing", reason: "Sleep disruption from apnea can worsen perceived tinnitus." },
  ],
  // Tinnitus
  "6260": [
    { code: "6100", name: "Hearing loss bilateral", system: "hearing", reason: "Tinnitus and hearing loss share the same noise exposure cause." },
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Tinnitus can trigger or worsen migraine headaches." },
    { code: "9411", name: "PTSD", system: "mental", reason: "Tinnitus can aggravate PTSD symptoms, especially sleep and hypervigilance." },
    { code: "9400", name: "Generalized anxiety disorder", system: "mental", reason: "Constant tinnitus can cause or worsen anxiety." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic tinnitus is associated with depression." },
  ],
  // Hearing loss
  "6100": [
    { code: "6260", name: "Tinnitus", system: "hearing", reason: "Tinnitus almost always accompanies noise-induced hearing loss." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Communication difficulty from hearing loss can cause depression." },
  ],
  // Migraine
  "8100": [
    { code: "9411", name: "PTSD", system: "mental", reason: "Migraines commonly co-occur with PTSD." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic migraines commonly cause secondary depression." },
    { code: "6260", name: "Tinnitus", system: "hearing", reason: "Migraine aura can include or worsen tinnitus." },
  ],
  // TBI
  "8045": [
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Post-traumatic headaches are common after TBI." },
    { code: "9411", name: "PTSD", system: "mental", reason: "TBI events frequently cause concurrent PTSD." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Depression is very common after TBI." },
    { code: "6260", name: "Tinnitus", system: "hearing", reason: "TBI commonly causes tinnitus." },
    { code: "6100", name: "Hearing loss", system: "hearing", reason: "Blast/impact TBI can cause hearing loss." },
    { code: "6204", name: "Vestibular disorders", system: "hearing", reason: "TBI commonly causes balance/vestibular problems." },
    { code: "8910", name: "Epilepsy, grand mal", system: "neurological", reason: "Post-traumatic epilepsy can develop after TBI." },
  ],
  // Hypertension
  "7101": [
    { code: "7005", name: "Coronary artery disease", system: "cardio", reason: "Hypertension is a primary risk factor for CAD." },
    { code: "7007", name: "Hypertensive heart disease", system: "cardio", reason: "Chronic hypertension causes heart muscle changes." },
    { code: "6006", name: "Retinopathy", system: "eye", reason: "Hypertension can cause hypertensive retinopathy." },
    { code: "7502", name: "Nephritis, chronic", system: "genitourinary", reason: "Hypertension damages kidney function over time." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "Hypertension and its medications commonly cause ED." },
  ],
  // Diabetes
  "7913": [
    { code: "8520", name: "Peripheral neuropathy (sciatic)", system: "neurological", reason: "Diabetic peripheral neuropathy is extremely common." },
    { code: "8515", name: "Median nerve / carpal tunnel", system: "neurological", reason: "Diabetes increases carpal tunnel risk." },
    { code: "8521", name: "Peroneal nerve", system: "neurological", reason: "Diabetic neuropathy commonly affects lower extremity nerves." },
    { code: "7101", name: "Hypertension", system: "cardio", reason: "Diabetes and hypertension frequently co-occur." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "Diabetes is a leading cause of ED." },
    { code: "6006", name: "Retinopathy", system: "eye", reason: "Diabetic retinopathy is a major complication." },
    { code: "7502", name: "Nephritis", system: "genitourinary", reason: "Diabetic nephropathy is a common complication." },
    { code: "7005", name: "Coronary artery disease", system: "cardio", reason: "Diabetes significantly increases cardiovascular risk." },
  ],
  // Shoulder
  "5201": [
    { code: "8515", name: "Median nerve / carpal tunnel", system: "neurological", reason: "Shoulder conditions can cause compensatory overuse leading to carpal tunnel." },
    { code: "5237", name: "Cervical strain", system: "musculoskeletal", reason: "Shoulder limitation can cause compensatory neck strain." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Chronic pain and limitation can cause depression." },
  ],
  // GERD
  "7206": [
    { code: "6602", name: "Asthma", system: "respiratory", reason: "GERD can trigger or worsen asthma symptoms." },
    { code: "6516", name: "Chronic laryngitis", system: "respiratory", reason: "Acid reflux causes chronic laryngeal inflammation." },
    { code: "7346", name: "Hiatal hernia", system: "digestive", reason: "Hiatal hernia commonly co-occurs with GERD." },
  ],
  // Flatfoot
  "5276": [
    { code: "5269", name: "Plantar fasciitis", system: "musculoskeletal", reason: "Flatfoot commonly causes secondary plantar fasciitis." },
    { code: "5271", name: "Ankle limitation", system: "musculoskeletal", reason: "Flatfoot alters ankle mechanics." },
    { code: "5260", name: "Knee limitation", system: "musculoskeletal", reason: "Altered gait from flatfoot affects knees." },
    { code: "5237", name: "Lumbar strain", system: "musculoskeletal", reason: "Altered gait chain from flatfoot causes back problems." },
  ],
  // Plantar fasciitis
  "5269": [
    { code: "5276", name: "Flatfoot / pes planus", system: "musculoskeletal", reason: "Plantar fasciitis and flatfoot commonly co-occur." },
    { code: "5271", name: "Ankle limitation", system: "musculoskeletal", reason: "Foot pain alters ankle mechanics." },
    { code: "5260", name: "Knee limitation", system: "musculoskeletal", reason: "Altered gait from foot pain can affect knees." },
  ],
  // Carpal tunnel
  "8515": [
    { code: "8516", name: "Ulnar nerve neuropathy", system: "neurological", reason: "Carpal tunnel often co-occurs with ulnar neuropathy." },
    { code: "5215", name: "Wrist limitation of motion", system: "musculoskeletal", reason: "Carpal tunnel surgery or condition can limit wrist motion." },
  ],
  // Fibromyalgia
  "5025": [
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Depression is extremely common with fibromyalgia." },
    { code: "6354", name: "Chronic fatigue syndrome", system: "infectious", reason: "CFS and fibromyalgia frequently overlap." },
    { code: "8100", name: "Migraine headaches", system: "neurological", reason: "Migraines are common in fibromyalgia patients." },
    { code: "7319", name: "IBS", system: "digestive", reason: "IBS is commonly comorbid with fibromyalgia." },
    { code: "6847", name: "Sleep apnea", system: "respiratory", reason: "Sleep disorders common with fibromyalgia." },
  ],
  // MS
  "8018": [
    { code: "7534", name: "Neurogenic bladder", system: "genitourinary", reason: "Bladder dysfunction is common in MS." },
    { code: "9434", name: "Major depressive disorder", system: "mental", reason: "Depression frequently accompanies MS." },
    { code: "6080", name: "Visual field defect", system: "eye", reason: "MS commonly affects vision." },
    { code: "7522", name: "Erectile dysfunction", system: "genitourinary", reason: "MS nerve damage can cause ED." },
  ],
};

export function getSecondarySuggestions(code, existingCodes) {
  const clean = String(code || "").split("/")[0].trim();
  const suggestions = secondaryMap[clean] || [];
  const existing = new Set(existingCodes.map((c) => String(c).split("/")[0].trim()));
  return suggestions.filter((s) => !existing.has(s.code));
}

/**
 * Aggregate co-occurrence hints across ALL conditions currently in the claim.
 * Returns a deduplicated, scored list of conditions the veteran has NOT yet added but that
 * commonly co-occur with one or more of their existing conditions.
 *
 * Score = number of existing issues that suggest this condition. Higher = more obvious gap.
 * Each entry also carries the list of "linkedFrom" primary issue claimedAs names so the UI can
 * show *why* it's being suggested ("from PTSD + Sleep Apnea").
 *
 * @param {Array} issues       - veteran's current claim issues, each at minimum { id, code, claimedAs }
 * @returns {Array<{ code, name, system, score, linkedFrom: Array<{ id, claimedAs, code, reason }> }>}
 */
export function getCoOccurrenceSummary(issues) {
  if (!Array.isArray(issues) || issues.length === 0) return [];
  const existingCodes = new Set(issues.map((i) => String(i.code || "").split("/")[0].trim()).filter(Boolean));
  // Map suggested-code → aggregator
  const aggregator = new Map();
  for (const primary of issues) {
    const primaryCode = String(primary.code || "").split("/")[0].trim();
    if (!primaryCode) continue;
    const suggestions = secondaryMap[primaryCode] || [];
    for (const s of suggestions) {
      if (existingCodes.has(s.code)) continue; // already claimed — skip
      const entry = aggregator.get(s.code) || {
        code: s.code,
        name: s.name,
        system: s.system,
        score: 0,
        linkedFrom: [],
      };
      entry.score += 1;
      entry.linkedFrom.push({
        id: primary.id,
        claimedAs: primary.claimedAs || `DC ${primaryCode}`,
        code: primaryCode,
        reason: s.reason,
      });
      aggregator.set(s.code, entry);
    }
  }
  // Sort: highest score first; tie-break alpha on name.
  return Array.from(aggregator.values()).sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (a.name || "").localeCompare(b.name || "");
  });
}

export default secondaryMap;
